// src/curated/tripEditor.js
// Everything that changes a curated trip lives here, as plain functions with no React:
//   applyOps()       the only way a trip gets edited (chat agent, hotel picker, buttons)
//   parseLocally()   rule-based fallback when the AI agent backend is off or down
//   runTripAgent()   calls the agent Lambda, falls back to parseLocally()
//   priceTrip()      engine price + hotel changes + activity tickets
//   tripSnapshot()   compact summary for the lead bot and ops
//
// Edit state: {
//   trip,
//   hotelPicks:  { [stayBlockId]: code }   shortlist code, or "live:<hotel_code>" for a /hotels API result
//   liveHotels:  { [stayBlockId]: hotel }  the picked live hotel (price, room, handoff data)
//   params:      { originName, startDate, pax, routePref }
// }
// Live search results are passed in separately as `live: { [stayBlockId]: hotel[] }` (not undoable state).
// Activities are never booked or charged by RouteChef: approx_pp is a hint shown as "pay at venue".

import { FEATURES } from "../config/features";

export const AGENT_URL = FEATURES.CURATED_TRIP_AGENT_URL || "";
const AGENT_TIMEOUT_MS = 20000;

const clone = (o) => JSON.parse(JSON.stringify(o));
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
let uid = 0;
const newId = () => `u${Date.now().toString(36)}${(uid++).toString(36)}`;

// ── lookups ────────────────────────────────────────────────────────────────
export const allBlocks = (trip) =>
    trip.days.flatMap((d, di) => d.blocks.map((b, bi) => ({ b, di, bi, day: d })));

export const findBlock = (trip, id) => allBlocks(trip).find((x) => x.b.id === id) || null;

export const stayBlocks = (trip) => allBlocks(trip).filter((x) => x.b.type === "STAY");

// Which stay covers the night after day index di (check-in day <= di < check-out day)
const stayForNight = (trip, di) => stayBlocks(trip).find((x) => x.di <= di && di < x.di + x.b.nights) || null;

export const hotelOptionsFor = (trip, city) => (trip.hotel_options && trip.hotel_options[city]) || [];

export const findHotel = (trip, code) => {
    for (const list of Object.values(trip.hotel_options || {})) {
        const h = list.find((x) => x.code === code);
        if (h) return h;
    }
    return null;
};

const fuzzyScore = (query, text) => {
    const q = norm(query), t = norm(text);
    if (!q || !t) return 0;
    if (t === q) return 100;
    if (t.includes(q)) return 80;
    const words = q.split(" ").filter((w) => w.length > 2);
    if (!words.length) return 0;
    const hit = words.filter((w) => t.includes(w)).length;
    return Math.round((hit / words.length) * 60);
};

/** Best block for a free-text reference ("the elephant camp", "raja's seat") */
export const matchBlock = (trip, query, types = ["STOP", "EXPERIENCE", "MOVE", "TIP"]) => {
    let best = null;
    for (const x of allBlocks(trip)) {
        if (!types.includes(x.b.type)) continue;
        const s = fuzzyScore(query, x.b.name || x.b.text || `${x.b.from} ${x.b.to}`);
        if (s > (best?.s || 0)) best = { ...x, s };
    }
    return best && best.s >= 40 ? best : null;
};

export const matchPool = (trip, query) => {
    let best = null;
    for (const p of trip.activity_pool || []) {
        const s = Math.max(fuzzyScore(query, p.name), ...((p.tags || []).map((t) => (norm(query).includes(t) ? 70 : 0))));
        if (s > (best?.s || 0)) best = { p, s };
    }
    return best && best.s >= 40 ? best.p : null;
};

// ── ops ────────────────────────────────────────────────────────────────────
// Every op is validated here, so the agent (local or LLM) can never break a trip.
// Supported:
//   { op: "add_activity", day, pool_id?, item?: { name, type, time, note, approx_pp } }
//   { op: "remove_block", block_id }
//   { op: "move_block", block_id, to_day }
//   { op: "set_time", block_id, time }
//   { op: "swap_hotel", stay_id, hotel_code }
//   { op: "set_hotel_tier", tier, stay_id? }
//   { op: "add_day", after_day? }
//   { op: "remove_day", day }
//   { op: "set_params", origin?, start_date?, pax?, route_pref? }
//   UI-only (no state change): { op: "open_hotel_picker", stay_id? } | { op: "handoff" }

const renumber = (trip) => { trip.days.forEach((d, i) => { d.day = i + 1; }); };
const dayIdx = (trip, day) => {
    const i = Number(day) - 1;
    return Number.isInteger(i) && i >= 0 && i < trip.days.length ? i : -1;
};
const sortByTime = (blocks) => {
    // STAY first, TIPs last, the rest by time
    const rank = (b) => (b.type === "STAY" ? -1 : b.type === "TIP" ? 2 : b.type === "MOVE" ? 0 : 1);
    return [...blocks].sort((a, b) => rank(a) - rank(b) || String(a.time || a.depart || "99").localeCompare(String(b.time || b.depart || "99")));
};
const label = (b) => b.name || b.text || `${b.from} → ${b.to}`;

export const pickBestInTier = (trip, city, tier) =>
    hotelOptionsFor(trip, city).filter((h) => h.tier === tier).sort((a, b) => b.rating - a.rating || a.price_pn - b.price_pn)[0] || null;

/**
 * @returns { state, changes: string[], rejected: string[], ui: op[] }
 */
// Live results for a stay, best rated first within a tier, then cheapest
const liveInTier = (live, stayId, tier) =>
    ((live && live[stayId]) || []).filter((h) => h.tier === tier).sort((a, b) => (b.rating || 0) - (a.rating || 0) || a.total - b.total)[0] || null;

export function applyOps(state, ops = [], { origins = [], live = {} } = {}) {
    const next = {
        trip: clone(state.trip),
        hotelPicks: { ...state.hotelPicks },
        liveHotels: { ...(state.liveHotels || {}) },
        params: { ...state.params },
    };
    const t = next.trip;
    const changes = [], rejected = [], ui = [];

    for (const o of ops || []) {
        if (!o || typeof o !== "object") continue;
        switch (o.op) {
            case "add_activity": {
                const di = dayIdx(t, o.day ?? t.days.length);
                if (di < 0) { rejected.push(`There's no day ${o.day} in this trip.`); break; }
                const src = o.pool_id ? (t.activity_pool || []).find((p) => p.id === o.pool_id) : o.item;
                if (!src || !src.name) { rejected.push("I couldn't work out what to add."); break; }
                if (allBlocks(t).some((x) => norm(x.b.name) === norm(src.name))) { rejected.push(`${src.name} is already in the plan.`); break; }
                const blk = {
                    id: newId(), type: src.type === "STOP" ? "STOP" : "EXPERIENCE",
                    name: String(src.name).slice(0, 80), time: src.time || "", note: src.note || "",
                    lock: "FLEXIBLE", approx_pp: Math.max(0, Number(src.approx_pp ?? src.price_pp) || 0), added: true,
                };
                t.days[di].blocks = sortByTime([...t.days[di].blocks, blk]);
                changes.push(`Added ${blk.name} on Day ${di + 1}`);
                break;
            }
            case "remove_block": {
                const x = findBlock(t, o.block_id);
                if (!x) { rejected.push("I couldn't find that in the plan."); break; }
                if (x.b.type === "STAY" || x.b.type === "MOVE") { rejected.push(`${label(x.b)} keeps the trip connected, so it can't be removed. You can change it instead.`); break; }
                if (x.b.lock === "LOCKED") { rejected.push(`${label(x.b)} is a curator pick and stays in. I can move it to another day.`); break; }
                t.days[x.di].blocks.splice(x.bi, 1);
                changes.push(`Removed ${label(x.b)}`);
                break;
            }
            case "move_block": {
                const x = findBlock(t, o.block_id);
                const to = dayIdx(t, o.to_day);
                if (!x || to < 0) { rejected.push("I couldn't move that."); break; }
                if (x.b.type === "STAY" || x.b.type === "MOVE") { rejected.push(`${label(x.b)} can't move days.`); break; }
                if (to === x.di) break;
                t.days[x.di].blocks.splice(x.bi, 1);
                t.days[to].blocks = sortByTime([...t.days[to].blocks, x.b]);
                changes.push(`Moved ${label(x.b)} to Day ${to + 1}`);
                break;
            }
            case "set_time": {
                const x = findBlock(t, o.block_id);
                if (!x || !/^\d{2}:\d{2}$/.test(o.time || "")) { rejected.push("I couldn't change that time."); break; }
                if (x.b.type === "MOVE") x.b.depart = o.time; else x.b.time = o.time;
                t.days[x.di].blocks = sortByTime(t.days[x.di].blocks);
                changes.push(`${label(x.b)} now at ${o.time}`);
                break;
            }
            case "swap_hotel": {
                const x = findBlock(t, o.stay_id);
                if (!x || x.b.type !== "STAY") { rejected.push("I couldn't find that stay."); break; }
                if (x.b.lock === "LOCKED") { rejected.push(`The ${x.b.city} stay is a curator pick and can't change.`); break; }
                if (o.hotel_code === "__live__") {
                    delete next.hotelPicks[x.b.id]; delete next.liveHotels[x.b.id];
                    changes.push(`Back to our matched hotel in ${x.b.city}`); break;
                }
                if (String(o.hotel_code || "").startsWith("live:")) {
                    const lh = ((live && live[x.b.id]) || []).find((h) => h.code === o.hotel_code)
                        || (next.liveHotels[x.b.id]?.code === o.hotel_code ? next.liveHotels[x.b.id] : null);
                    if (!lh) { rejected.push(`That hotel isn't available in ${x.b.city} for your dates any more.`); break; }
                    next.hotelPicks[x.b.id] = lh.code; next.liveHotels[x.b.id] = lh;
                    changes.push(`${x.b.city} stay: ${lh.name} (book instantly)`); break;
                }
                const h = findHotel(t, o.hotel_code);
                if (!h || !hotelOptionsFor(t, x.b.city).includes(h)) { rejected.push(`That hotel isn't on our ${x.b.city} list.`); break; }
                next.hotelPicks[x.b.id] = h.code; delete next.liveHotels[x.b.id];
                changes.push(`${x.b.city} stay: ${h.name}`);
                break;
            }
            case "set_hotel_tier": {
                const targets = o.stay_id ? stayBlocks(t).filter((x) => x.b.id === o.stay_id) : stayBlocks(t);
                let n = 0;
                for (const x of targets) {
                    if (x.b.lock === "LOCKED") continue;
                    // Prefer a live, instantly bookable hotel in that tier; else the curator shortlist
                    const lh = liveInTier(live, x.b.id, o.tier);
                    if (lh) { next.hotelPicks[x.b.id] = lh.code; next.liveHotels[x.b.id] = lh; n += 1; changes.push(`${x.b.city} stay: ${lh.name} (book instantly)`); continue; }
                    const h = pickBestInTier(t, x.b.city, o.tier);
                    if (h) { next.hotelPicks[x.b.id] = h.code; delete next.liveHotels[x.b.id]; n += 1; changes.push(`${x.b.city} stay: ${h.name}`); }
                }
                if (!n) rejected.push(`No ${o.tier} options on our list for this trip.`);
                break;
            }
            case "add_day": {
                // New free day inserted after after_day (default: the day before the last day),
                // the stay covering that night gets one more night, the trip back moves a day.
                const after = dayIdx(t, o.after_day ?? Math.max(1, t.days.length - 1));
                if (after < 0) { rejected.push("I couldn't add a day there."); break; }
                if (t.days.length >= 10) { rejected.push("10 days is the longest we plan online. Our team can do longer trips."); break; }
                const stay = stayForNight(t, after) || stayBlocks(t).find((x) => x.di + x.b.nights === after + 1);
                if (!stay) { rejected.push("There's no hotel to extend for that night."); break; }
                stay.b.nights += 1;
                t.days.splice(after + 1, 0, { day: 0, title: `Free day in ${stay.b.city}`, blocks: [] });
                renumber(t);
                if (t.exit.day_offset >= after + 1) t.exit.day_offset += 1;
                changes.push(`Added a free day after Day ${after + 1} (+1 night in ${stay.b.city})`);
                break;
            }
            case "remove_day": {
                const di = dayIdx(t, o.day);
                if (di <= 0) { rejected.push(di === 0 ? "Day 1 can't go, that's when the trip starts." : `There's no day ${o.day}.`); break; }
                if (t.days.length <= 2) { rejected.push("That's already the shortest version of this trip."); break; }
                const blocks = t.days[di].blocks;
                if (blocks.some((b) => b.lock === "LOCKED" || b.type === "STAY" || b.type === "MOVE")) {
                    rejected.push(`Day ${di + 1} has a check-in, a transfer or a curator pick, so it can't be dropped. Try removing the things you don't want instead.`); break;
                }
                const stay = stayForNight(t, di - 1);
                if (!stay || stay.b.nights <= 1) { rejected.push(`Removing Day ${di + 1} would leave a stay with no nights.`); break; }
                stay.b.nights -= 1;
                const moved = blocks.filter((b) => b.type !== "TIP").map((b) => b.name);
                t.days.splice(di, 1);
                renumber(t);
                if (t.exit.day_offset >= di) t.exit.day_offset = Math.max(0, t.exit.day_offset - 1);
                changes.push(`Removed Day ${di + 1}${moved.length ? ` (dropped ${moved.join(", ")})` : ""}`);
                break;
            }
            case "set_params": {
                if (o.origin) {
                    const c = origins.find((x) => norm(x.name) === norm(o.origin)) || origins.find((x) => norm(x.name).includes(norm(o.origin)));
                    if (c) { next.params.originName = c.name; changes.push(`Starting from ${c.name}`); }
                    else rejected.push(`We don't plan from ${o.origin} yet. Try ${origins.slice(0, 4).map((x) => x.name).join(", ")}.`);
                }
                if (o.start_date) {
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const d = new Date(`${o.start_date}T00:00:00`);
                    if (isNaN(d) || d <= today) rejected.push("Pick a date from tomorrow onwards.");
                    else { next.params.startDate = o.start_date; changes.push(`Day 1 on ${d.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`); }
                }
                if (o.pax != null) {
                    const n = Math.round(Number(o.pax));
                    if (n >= 1 && n <= (t.max_group || 8)) { next.params.pax = n; changes.push(`${n} traveller${n > 1 ? "s" : ""}`); }
                    else rejected.push(`This trip is for 1 to ${t.max_group || 8} people online. Our team can plan bigger groups.`);
                }
                if (o.route_pref && ["best", "cheapest", "fastest"].includes(o.route_pref)) {
                    next.params.routePref = o.route_pref; changes.push(`Travel optimised for ${o.route_pref}`);
                }
                break;
            }
            case "open_hotel_picker":
            case "handoff":
                ui.push(o);
                break;
            default:
                break;
        }
    }
    return { state: next, changes, rejected, ui };
}

// ── local parser (no LLM) ──────────────────────────────────────────────────
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const NUMBER_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };
const toNum = (s) => (NUMBER_WORDS[s] || Number(s));

const parseDate = (text) => {
    const s = text.toLowerCase();
    let m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    m = s.match(/(\d{1,2})(?:st|nd|rd|th)?\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*/) ||
        s.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(\d{1,2})/);
    if (!m) return null;
    const [day, mon] = /^\d/.test(m[1]) ? [Number(m[1]), m[2]] : [Number(m[2]), m[1]];
    const now = new Date();
    let y = now.getFullYear();
    const mi = MONTHS.indexOf(mon);
    if (new Date(y, mi, day) <= now) y += 1;
    return `${y}-${String(mi + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const dayRef = (s) => {
    const m = s.match(/\bday\s*(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten)\b/);
    if (m) return toNum(m[1]);
    if (/\b(first day|day one)\b/.test(s)) return 1;
    if (/\blast day\b/.test(s)) return -1;
    return null;
};

/**
 * Small rule-based agent. Handles the common asks so the page works without the backend.
 * Returns { reply, ops, understood }
 */
export function parseLocally(message, state, { origins = [], live = {} } = {}) {
    const raw = String(message || "").trim();
    const s = raw.toLowerCase();
    const t = state.trip;
    const ops = [];
    const resolveDay = (d) => (d === -1 ? t.days.length : d);

    if (!s) return { reply: "", ops: [], understood: false };

    if (/\b(talk|speak|call)\b.*\b(person|human|someone|expert|agent|team)\b|\bcall me\b|\bcallback\b/.test(s)) {
        return { reply: "Sure, connecting you with our travel team.", ops: [{ op: "handoff" }], understood: true };
    }

    // Travellers
    const pax = s.match(/\b(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten)\s*(people|persons|pax|travell?ers|adults|of us|friends)\b/) ||
        s.match(/\bwe are\s*(\d{1,2}|two|three|four|five|six|seven|eight)\b/);
    if (pax) ops.push({ op: "set_params", pax: toNum(pax[1]) });
    if (/\b(solo|just me|alone)\b/.test(s)) ops.push({ op: "set_params", pax: 1 });
    if (/\b(couple|my (wife|husband|partner))\b/.test(s) && !pax) ops.push({ op: "set_params", pax: 2 });

    // Origin
    const from = s.match(/\b(?:from|starting (?:from|in)|leaving from|we're in|i'm in|i am in)\s+([a-z ]{3,20}?)(?:\s+(?:on|to|for|with|and|instead)\b|[,.!?]|$)/);
    if (from) {
        const c = origins.find((x) => norm(from[1]).includes(norm(x.name)) || norm(x.name).startsWith(norm(from[1])));
        if (c && c.name !== state.params.originName) ops.push({ op: "set_params", origin: c.name });
    }

    // Date
    const date = parseDate(s);
    if (date && /\b(on|from|start|leave|go|travel|date|day 1)\b/.test(s)) ops.push({ op: "set_params", start_date: date });

    // Route preference
    if (/\b(cheapest|cheaper) (way|travel|route|flight|train|bus)\b|\bon a budget\b/.test(s)) ops.push({ op: "set_params", route_pref: "cheapest" });
    if (/\b(fastest|quickest|faster)\b/.test(s)) ops.push({ op: "set_params", route_pref: "fastest" });

    // Hotels
    const anyStay = stayBlocks(t)[0];
    const cityStay = stayBlocks(t).find((x) => s.includes(norm(x.b.city)))
        || (/houseboat/.test(s) ? stayBlocks(t).find((x) => hotelOptionsFor(t, x.b.city).some((h) => /houseboat/i.test(h.name))) : null)
        || null;
    if (/\b(cheap|cheaper|budget|affordable|less expensive)\b.*\b(hotel|stay|room|place|houseboat)s?\b|\b(hotel|stay)s?\b.*\b(cheap|cheaper|budget)\b|\bhostel\b/.test(s)) {
        ops.push({ op: "set_hotel_tier", tier: "budget", ...(cityStay ? { stay_id: cityStay.b.id } : {}) });
    } else if (/\b(luxury|luxurious|premium|5 ?star|five star|nicer|fancier|upgrade|better)\b.*\b(hotel|stay|room|resort|houseboat)s?\b|\b(hotel|stay)s?\b.*\b(luxury|premium|upgrade)\b|\bupgrade\b/.test(s)) {
        ops.push({ op: "set_hotel_tier", tier: "premium", ...(cityStay ? { stay_id: cityStay.b.id } : {}) });
    } else if (/\b(change|swap|switch|other|different|show)\b.*\b(hotel|stay|houseboat|resort)s?\b/.test(s)) {
        // Named hotel from the shortlist?
        let named = null;
        for (const x of stayBlocks(t)) {
            for (const h of [...((live && live[x.b.id]) || []), ...hotelOptionsFor(t, x.b.city)]) if (fuzzyScore(h.name, s) >= 60 || s.includes(norm(h.name))) named = { x, h };
        }
        if (named) ops.push({ op: "swap_hotel", stay_id: named.x.b.id, hotel_code: named.h.code });
        else ops.push({ op: "open_hotel_picker", stay_id: (cityStay || anyStay)?.b.id });
    }

    // Days
    const addToDay = /\b(add|include)\b.*\b(to|on|in|for)\s+(day\s*\w+|first day|last day)\b/.test(s);
    const addDay = !addToDay && /\b(add|one more|extra|another|extend)\b.*\bday\b|\bmake it (\d+|four|five|six) days\b|\blonger\b/.test(s);
    const removeDay = /\b(remove|drop|cut|skip|delete)\b.*\bday\s*\d\b|\bshorter\b|\bone day less\b/.test(s);
    if (addDay) {
        const m = s.match(/make it (\d+|four|five|six) days/);
        const want = m ? toNum(m[1]) - t.days.length : 1;
        for (let i = 0; i < Math.min(Math.max(want, 1), 4); i += 1) ops.push({ op: "add_day" });
    } else if (removeDay) {
        const d = dayRef(s);
        ops.push({ op: "remove_day", day: d ? resolveDay(d) : Math.max(2, t.days.length - 1) });
    }

    // Remove / move / add things
    const rm = s.match(/\b(?:remove|skip|drop|delete|cancel|no|don't want|dont want|take out)\s+(?:the\s+)?(.+?)(?:\s+from day\s*\d+)?$/);
    const mv = s.match(/\b(?:move|shift|put)\s+(?:the\s+)?(.+?)\s+(?:to|on|onto)\s+day\s*(\d+|one|two|three|four|five)/);
    const add = s.match(/\b(?:add|include|i want|can we do|let's do|book)\s+(?:a\s+|an\s+|some\s+)?(.+?)(?:\s+(?:on|to|for)\s+(day\s*\w+|first day|last day))?$/);
    if (mv) {
        const hit = matchBlock(t, mv[1]);
        if (hit) ops.push({ op: "move_block", block_id: hit.b.id, to_day: toNum(mv[2]) });
    } else if (rm && !removeDay && !/\bhotel|stay\b/.test(rm[1])) {
        const hit = matchBlock(t, rm[1]);
        if (hit) ops.push({ op: "remove_block", block_id: hit.b.id });
    } else if (add && !addDay && !/\bhotel|stay|room\b/.test(add[1])) {
        const p = matchPool(t, add[1]);
        const d = dayRef(add[2] || s);
        // default: the first day that isn't the travel-home day and has room
        const fallbackDay = t.days.reduce((best, d0, i) => (i < t.days.length - 1 && d0.blocks.length < t.days[best].blocks.length ? i : best), 0) + 1;
        if (p) ops.push({ op: "add_activity", day: d ? resolveDay(d) : fallbackDay, pool_id: p.id });
    }

    // Interest-based asks ("something for kids", "more adventure")
    if (!ops.length) {
        const p = matchPool(t, s);
        if (p && /\b(something|anything|more|kids|adventure|relax|food|sunrise|spa|trek|water)\b/.test(s)) {
            ops.push({ op: "add_activity", day: Math.max(1, t.days.length - 1), pool_id: p.id });
        }
    }

    if (!ops.length) {
        return {
            reply: "I can change hotels, add or drop activities, add or remove a day, or change where you start, the date and how many of you are going. Try one of the suggestions below.",
            ops: [], understood: false,
        };
    }
    return { reply: "", ops, understood: true };
}

// ── agent client ───────────────────────────────────────────────────────────
const compactTrip = (state, live = {}) => ({
    slug: state.trip.slug,
    title: state.trip.title,
    max_group: state.trip.max_group,
    params: state.params,
    days: state.trip.days.map((d) => ({
        day: d.day, title: d.title,
        blocks: d.blocks.map((b) => ({
            id: b.id, type: b.type, name: label(b), time: b.time || b.depart || "", lock: b.lock || "",
            ...(b.type === "STAY" ? { city: b.city, nights: b.nights, hotel: state.hotelPicks[b.id] || "live" } : {}),
        })),
    })),
    // Live hotels (bookable now) first, then the curator shortlist (confirmed by our team)
    hotel_options: Object.fromEntries(Object.entries(state.trip.hotel_options || {}).map(([city, list]) => {
        const stayIds = stayBlocks(state.trip).filter((x) => x.b.city === city).map((x) => x.b.id);
        const liveList = stayIds.flatMap((id) => (live && live[id]) || []).slice(0, 8)
            .map((h) => ({ code: h.code, name: h.name, tier: h.tier, price_pn: h.price_pn, rating: h.rating, bookable_now: true }));
        return [city, [...liveList, ...list.map((h) => ({ code: h.code, name: h.name, tier: h.tier, price_pn: h.price_pn, rating: h.rating, bookable_now: false }))]];
    })),
    // Not booked by RouteChef: travellers pay at the venue. approx_pp is only a rough hint.
    activity_pool: (state.trip.activity_pool || []).map((p) => ({ id: p.id, name: p.name, type: p.type, time: p.time, approx_pp: p.approx_pp || 0, pay_at_venue: true, tags: p.tags })),
});

/**
 * @returns { reply, ops, suggestions?, source: "agent" | "local", understood }
 */
export async function runTripAgent({ message, state, history = [], origins = [], live = {} }) {
    if (AGENT_URL) {
        const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
        const timer = ctrl ? setTimeout(() => ctrl.abort(), AGENT_TIMEOUT_MS) : null;
        try {
            const res = await fetch(AGENT_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                signal: ctrl?.signal,
                body: JSON.stringify({
                    intent: "curated_trip_edit",
                    message: String(message).slice(0, 600),
                    history: history.slice(-8).map((m) => ({ role: m.from === "user" ? "user" : "assistant", text: String(m.text).slice(0, 600) })),
                    trip: compactTrip(state, live),
                    origins: origins.map((o) => o.name),
                    today: new Date().toISOString().slice(0, 10),
                }),
            });
            let json = await res.json();
            if (json && typeof json.body === "string") json = JSON.parse(json.body);
            const data = json?.data || json;
            if (data && (Array.isArray(data.ops) || data.reply)) {
                return { reply: data.reply || "", ops: Array.isArray(data.ops) ? data.ops : [], suggestions: data.suggestions, source: "agent", understood: data.understood !== false };
            }
        } catch { /* fall through to the local parser */ } finally { if (timer) clearTimeout(timer); }
    }
    return { ...parseLocally(message, state, { origins, live }), source: "local" };
}

// ── pricing ────────────────────────────────────────────────────────────────
/**
 * The engine prices travel + stays at its own (live or fallback) hotel. On top of that,
 * a picked hotel replaces the engine's stay total for that block:
 *  - live pick: the exact /hotels API total for the stay (bookable now)
 *  - shortlist pick: price_pn x nights x rooms (estimate, confirmed by our team)
 * Activities are NOT included. They're paid at the venue; payAtVenue is a rough hint only.
 */
export function priceTrip(inst, state) {
    if (!inst) return null;
    const pax = state.params.pax;
    const t = state.trip;
    const rooms = inst.rooms || Math.ceil(pax / 2);
    let hotelDelta = 0;
    const stays = stayBlocks(t).map((x) => {
        const engine = (inst.stays || []).find((s) => s.block && s.block.dayIndex === x.di && s.block.city === x.b.city) || null;
        const code = state.hotelPicks[x.b.id];
        const lh = code && code.startsWith("live:") ? (state.liveHotels || {})[x.b.id] || null : null;
        const h = code && !lh ? findHotel(t, code) : null;
        const engineTotal = engine ? engine.total : x.b.fallback_pn * x.b.nights * rooms;
        const total = lh ? lh.total : h ? h.price_pn * x.b.nights * rooms : engineTotal;
        hotelDelta += total - engineTotal;
        return {
            id: x.b.id, city: x.b.city, nights: x.b.nights, dayIndex: x.di, rooms,
            name: lh ? lh.name : h ? h.name : (engine?.name || x.b.preferred || `Stay in ${x.b.city}`),
            total, picked: lh || h, live: lh, engine,
            estimated: lh ? false : h ? true : !!engine?.estimated,
            liveBookable: !!lh || (!h && !!engine?.hotel?.hotel_code),
        };
    });
    const payAtVenue = allBlocks(t)
        .filter((x) => x.b.type === "EXPERIENCE" && Number(x.b.approx_pp) > 0)
        .map((x) => ({ id: x.b.id, name: x.b.name, day: x.di + 1, approx_pp: Number(x.b.approx_pp) }));
    const total = Math.round(inst.price.total + hotelDelta);
    return {
        total, perPerson: Math.round(total / pax), rooms, stays, hotelDelta, payAtVenue,
        lines: [
            ...(inst.price.lines || []),
            ...(hotelDelta ? [{ k: "hotel_changes", label: "Hotel changes", amount: hotelDelta, estimated: stays.some((s) => s.picked && !s.live) }] : []),
        ],
        fees: (inst.price.markup || 0) + (inst.price.fees || 0),
        anyEstimated: inst.anyEstimated || stays.some((s) => s.estimated),
    };
}

// ── snapshot for the lead bot / ops ────────────────────────────────────────
export function tripSnapshot(state, price, original) {
    const t = state.trip;
    const start = state.params.startDate;
    const end = (() => {
        const d = new Date(`${start}T00:00:00`); d.setDate(d.getDate() + t.exit.day_offset);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    })();
    const origIds = new Set(original ? allBlocks(original).map((x) => x.b.id) : []);
    const curIds = new Set(allBlocks(t).map((x) => x.b.id));
    const edits = (original ? [...curIds].filter((id) => !origIds.has(id)).length + [...origIds].filter((id) => !curIds.has(id)).length : 0)
        + Object.keys(state.hotelPicks).length + (original && original.days.length !== t.days.length ? 1 : 0);
    const snap = {
        key: t.slug,
        slug: t.slug,
        title: t.title,
        origin: state.params.originName,
        entry_city: t.entry.city,
        exit_city: t.exit.city,
        start_date: start,
        end_date: end,
        days_count: t.days.length,
        pax: state.params.pax,
        total: price?.total || 0,
        per_person: price?.perPerson || 0,
        hotels: (price?.stays || []).map((s) => ({ city: s.city, name: s.name, nights: s.nights, bookable_now: !!s.live, hotel_code: s.live?.hotel_code || "" })),
        pay_at_venue: (price?.payAtVenue || []).map((a) => a.name),
        days: t.days.map((d) => ({ day: d.day, title: d.title, items: d.blocks.filter((b) => b.type !== "TIP" && b.type !== "STAY").map(label) })),
        edits,
    };
    snap.summary = snapshotText(snap);
    return snap;
}

/** Plain-English trip note for the ops team (goes in the lead's message field). */
export function snapshotText(snap) {
    if (!snap) return "";
    const lines = [
        `${snap.title}: ${snap.days_count} days from ${snap.origin}, ${snap.pax} traveller${snap.pax > 1 ? "s" : ""}, ${snap.start_date} to ${snap.end_date}.`,
        `Stays: ${snap.hotels.map((h) => `${h.name} (${h.city}, ${h.nights}N${h.bookable_now ? `, live ${h.hotel_code}` : ", confirm with hotel"})`).join("; ")}.`,
        ...snap.days.map((d) => `Day ${d.day}: ${d.items.join(", ") || "free day"}.`),
        ...(snap.pay_at_venue?.length ? [`Paid activities, not booked by us, traveller pays at venue: ${snap.pay_at_venue.join(", ")}.`] : []),
        `Quoted total Rs ${Number(snap.total).toLocaleString("en-IN")}${snap.edits ? `, ${snap.edits} edit${snap.edits > 1 ? "s" : ""} from the original plan` : ""}.`,
    ];
    return lines.join(" ");
}
